import type {ColorName} from 'shells/colors/colors';
import {getColorNameFromTagColor} from 'shells/colors/helpers';

import {LeadAssigneeRuleEnum} from 'nutshell-graphql-types';
import type {GetMoneyPage_maUrl_automation_actions as AutomationAction} from 'nutshell-graphql-types';

import {AUTO_RESOLVE_ID, SAME_ASSIGNEE_ID} from '../../core/form/fields/consts';
import type {Option} from '../../core/select/graphql-select-tags';

import type {SelectedTag} from './selects/tag-select-list';

export function getInitialTagsFromAction(
    action: AutomationAction | null | undefined
): SelectedTag[] {
    if (!action || action.config.__typename !== 'ActionConfigAddTags') {
        return [];
    }

    return action.config.tags.map((tag) => ({
        id: tag.id,
        label: tag.name,
        colorName: getColorNameFromTagColor(tag.color) as ColorName,
    }));
}

export function getInitialAudienceIdFromAction(
    action: AutomationAction | null | undefined
): string | null {
    if (!action || action.config.__typename !== 'ActionConfigAddAudiences') {
        return null;
    }

    return action.config.audiences[0]?.id || null;
}

export function getInitialRemoveAudienceIdFromAction(
    action: AutomationAction | null | undefined
): string | null {
    if (!action || action.config.__typename !== 'ActionConfigRemoveAudiences') {
        return null;
    }

    return action.config.audiences[0]?.id || null;
}

export function getInitialDripSequenceIdFromAction(
    action: AutomationAction | null | undefined
): string | null {
    if (!action || action.config.__typename !== 'ActionConfigAddToDripSequence') {
        return null;
    }

    return action.config.dripSequence.id;
}

export function getInitialPipelineIdFromAction(
    action: AutomationAction | null | undefined
): string | null {
    if (!action || action.config.__typename !== 'ActionConfigCreateLead') {
        return null;
    }

    return action.config.stageset?.id || null;
}

export function getInitialAssigneeIdFromAction(
    action: AutomationAction | null | undefined
): string | null {
    if (!action || action.config.__typename !== 'ActionConfigCreateLead') {
        return null;
    }

    const assigneeOrRule = action.config.assigneeOrRule;
    if (!assigneeOrRule) {
        return null;
    }

    if ('id' in assigneeOrRule) {
        return (assigneeOrRule as {id: string}).id;
    }

    if ('assignmentRule' in assigneeOrRule) {
        const rule = (assigneeOrRule as {assignmentRule: LeadAssigneeRuleEnum}).assignmentRule;

        // Convert GraphQL enum to frontend constant IDs
        if (rule === LeadAssigneeRuleEnum.AUTO_ASSIGN) {
            return AUTO_RESOLVE_ID;
        } else if (rule === LeadAssigneeRuleEnum.ASSIGN_TO_CONTACT_ASSIGNEE) {
            return SAME_ASSIGNEE_ID;
        }
    }

    return null;
}

export function getInitialLeadTagsFromAction(
    action: AutomationAction | null | undefined
): Option[] {
    if (!action || action.config.__typename !== 'ActionConfigCreateLead') {
        return [];
    }

    if (!action.config.tags) {
        return [];
    }

    return action.config.tags.map((tag) => ({
        id: tag.id,
        name: tag.name,
        colorName: 'offWhiteDk',
    }));
}

export function getAssigneeIdOrRule(assigneeId: string): any {
    if (assigneeId === AUTO_RESOLVE_ID) {
        return {assignmentRule: LeadAssigneeRuleEnum.AUTO_ASSIGN};
    }

    if (assigneeId === SAME_ASSIGNEE_ID) {
        return {assignmentRule: LeadAssigneeRuleEnum.ASSIGN_TO_CONTACT_ASSIGNEE};
    }

    return {assignee: assigneeId};
}
